import React, { ReactNode, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { CloseOutlineIcon } from 'cokitchen-icons';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import {
  Button,
  Input,
  ToastMessage,
  Textarea,
  FileUploader
} from '@cokitchen/cokitchen-components';
import { SearchType } from 'utils/types';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { ENUM_SEARCH_TYPE } from 'utils/enum';
import { getFilterName } from 'utils/functions';
import { STORAGE_KEYS } from 'utils/constants';

type EditBulkModalProps = {
  show: boolean;
  children?: ReactNode;
  onClose: (refetchData?: boolean) => void;
  data: SearchType[];
  refreshData?: () => void;
};
type PayloadType = {
  name: string;
  amount: string;
  images?: string[];
  summary: string;
};
const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '10px',
    backgroundColor: '#fff',
    border: 'none',
    padding: '0px',
    overflow: 'unset',
    maxWidth: '400px',
    width: '60%'
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(3px)',
    zIndex: 10000
  }
};

const EditBulkModal = ({ show, onClose, data, refreshData }: EditBulkModalProps) => {
  const [loading, setLoading] = useState(false);
  const [imageURL, setImageUrl] = useState<string[]>([]);
  const [formData, setFormData] = useState({} as PayloadType);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  const { pathname } = useLocation();

  const searchFilter = pathname.split('/')[3];

  const getPayload = () => {
    let payload = {};
    const mealIds = data.map((item) => item.id);
    for (const [property, value] of Object.entries(formData)) {
      if (!value || (Array.isArray(value) && !value.length) || value[0] === '') {
        delete formData[property as keyof PayloadType];
      }
    }
    if (searchFilter === ENUM_SEARCH_TYPE.MEAL) {
      payload = { ...formData, meal_ids: mealIds };
    } else if (searchFilter === ENUM_SEARCH_TYPE.MEAL_CATEGORY) {
      delete formData.images;
      payload = { ...formData, meal_category_ids: mealIds };
    } else if (searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY) {
      payload = { ...formData, meal_addon_category_ids: mealIds };
    }
    return payload;
  };

  const getEndpointUrl = () => {
    let url = '';
    if (searchFilter === ENUM_SEARCH_TYPE.MEAL) {
      url = 'marketing/meal-new';
    } else if (searchFilter === ENUM_SEARCH_TYPE.MEAL_CATEGORY) {
      url = 'marketing/bulk-update-meal-category';
    } else if (searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY) {
      url = 'marketing/bulk-update-meal-addon-category';
    }
    return url;
  };

  const editMeal = async () => {
    setLoading(true);
    try {
      const res = await apiService.put(getEndpointUrl(), { data: getPayload() });

      if (res?.data) {
        refreshData?.();
        toast(
          <ToastMessage
            type='success'
            message={`Successfully updated ${getFilterName(pathname)}`}
          />
        );
        onClose(true);
        setFormData({} as PayloadType);
        setImageUrl([]);
      }
    } catch {
      toast(
        <ToastMessage
          type='error'
          message={`Error encountered while updating ${getFilterName(pathname)}`}
        />
      );
    }
    setLoading(false);
  };
  const isCategory = () => {
    return (
      searchFilter === ENUM_SEARCH_TYPE.MEAL_CATEGORY ||
      searchFilter === ENUM_SEARCH_TYPE.ADDON_CATEGORY
    );
  };

  useEffect(() => {
    if (imageURL[0] !== '') setFormData({ ...formData, images: imageURL });
  }, [imageURL]);

  return (
    <div>
      <Modal isOpen={show} style={customStyles} ariaHideApp={false}>
        <div className='pb-8 px-5 space-y-3 text-[#1A1A1A] bg-white rounded-[10px]'>
          <div>
            <div className='flex justify-between items-center border-b border-solid border-[#EFF0F0] py-3 -mx-[1.2rem] '>
              <div></div>
              <h2 className='font-semibold'>Edit {getFilterName(pathname)}</h2>
              <div className='flex justify-center items-center rounded-full bg-[#EFF0F0] mr-3 w-5 h-5'>
                <CloseOutlineIcon className='cursor-pointer ' onClick={() => onClose()} />
              </div>
            </div>
            <div className='flex justify-center items-center gap-2 text-[12px] text-[#85806F] bg-[#EFF0F0] py-3 -mx-[1.2rem] '>
              <WarningIcon className='' />
              <p>You may choose to edit a single item off this list</p>
            </div>
          </div>

          <div>
            <p className='font-semibold text-[14px] capitalize'>{getFilterName(pathname)} Name</p>
            <Input
              name={`${getFilterName(pathname)} name`}
              small
              inputClass='h-12 placeholder:text-sm'
              className='text-[14px] '
              placeholder='Enter name'
              value={formData?.name}
              onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          {isCategory() && (
            <div>
              <p className='font-semibold text-[14px] capitalize'>
                {getFilterName(pathname)} summary
              </p>
              <Textarea
                placeholder='Enter summary'
                className='h-34 placeholder:text-sm'
                value={formData?.amount}
                onChange={(e: any) => setFormData({ ...formData, summary: e.target.value })}
              />
            </div>
          )}

          {!isCategory() && (
            <>
              <div className='relative'>
                <p className='font-semibold text-[14px]'>Price</p>
                <Input
                  type='tel'
                  placeholder='Enter meal amount'
                  inputClass='h-12 placeholder:text-sm'
                  value={formData?.amount}
                  onChange={(e: any) => setFormData({ ...formData, amount: e.target.value })}
                />
                <div className='bg-[#F2F2F3] rounded-md w-[42px] h-[27px] flex justify-center items-center absolute top-10 right-4 z-50'>
                  <p className='text-xs'>NGN</p>
                </div>
              </div>
              <div>
                <FileUploader
                  token={token}
                  fileUrls={imageURL}
                  setFileUrls={setImageUrl}
                  label={<h3 className='font-medium'>Upload Image</h3>}
                />
              </div>
            </>
          )}
          <div className='flex justify-center '>
            <Button
              onClick={editMeal}
              loading={loading}
              className=' h-[40px]'
              color='primary'
              variant='block'>
              Update
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditBulkModal;
